import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DmcPackageService } from 'src/app/business-service/dmc-package/dmc-package.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';

@Component({
  selector: 'app-dmc-optinal-sight-seeing',
  templateUrl: './dmc-optinal-sight-seeing.component.html',
  styleUrls: ['./dmc-optinal-sight-seeing.component.scss'],
})
export class DmcOptinalSightSeeingComponent implements OnInit {

  @Input() packageId: any;
  @Input() storedFormData: any;
  @Input() packageDetailsFormData: any;
  destinationsArray: any[] = [];

  @Input() editPackageId: any;

  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter<any>();

  sightSeeingForm!: FormGroup;

  formEnabled: boolean = true;

  constructor(
    private _fb: FormBuilder,
    private _globalService: GlobalService,
    private _responseNotificationService: ResponseNotificationService,
    private _dmcPackageService: DmcPackageService
  ) { }

  ngOnInit() {
    this.assignPackageDetailsData();
    this.sightSeeingForm = this._fb.group({
      packageId: [this.packageId],
      id: [null],
      organizationId: [null],
      createDate: [null],
      createdBy: [null],
      ownership: [''],
      locations: this._fb.array([this.createLocationFormGroup()])
    });

    this.sightSeeingForm.valueChanges.subscribe(() => {
      this.sightSeeingForm.markAsTouched();
      this.sightSeeingForm.markAsDirty();
    })

    if (this.storedFormData) {
      this.patchSightSeeingPackage(this.storedFormData);
    }
  }

  assignPackageDetailsData() {
    this.destinationsArray = this.packageDetailsFormData.destinations.map((item: any) => item.location)
  }
  createLocationFormGroup(): FormGroup {
    let data = this._fb.group({
      locationName: ['', [Validators.required]],
      visitingPlaces: this._fb.array([this.createVisitingPlacesFormGroup()])
    })
    return data;
  }

  createVisitingPlacesFormGroup(): FormGroup {
    let data = this._fb.group({
      place: ['', [Validators.required]],
      cost: [null, [Validators.required]]
    })
    return data;
  }

  getLocationsArray(): FormArray {
    return this.sightSeeingForm.get('locations') as FormArray;
  }

  getVisitingPlacesArray(locationIndex: number): FormArray {
    return this.getLocationsArray().at(locationIndex).get('visitingPlaces') as FormArray;
  }

  addVisitingPlaces(locationIndex: number) {
    this.getVisitingPlacesArray(locationIndex).push(this.createVisitingPlacesFormGroup());
  }

  addLocation() {
    this.getLocationsArray().push(this.createLocationFormGroup());
  }

  removeVisitingPlaces(locationIndex: number, visitingPlaceIndex: number) {
    this.getVisitingPlacesArray(locationIndex).removeAt(visitingPlaceIndex);
  }

  removeAllVisitingPlaces(locationIndex: number) {
    this.getVisitingPlacesArray(locationIndex).clear();
    this.addVisitingPlaces(locationIndex);
  }

  removeLocation(locationIndex: number) {
    this.getLocationsArray().removeAt(locationIndex);
  }

  //  Location change funtion
  onLocationChange(event: any, locationIndex: number) {
    let value = event.target.value;
    this.getLocationsArray().at(locationIndex).get('locationName')?.setValue(value);
  }

  //  Patching data funtions
  patchSightSeeingPackage(formData: any) {
    this.formEnabled = formData.enableSightseeing;
    // this.sightSeeingForm.patchValue(formData);
    const { locations, ...remainingData } = formData;
    this.sightSeeingForm.patchValue(remainingData);

    this.getLocationsArray().clear();
    locations.forEach((element: any, locationIndex: number) => {
      let locationGroup = this.createLocationFormGroup();
      locationGroup.patchValue({
        locationName: element.locationName,
      });
      this.getLocationsArray().push(locationGroup);
      this.patchVisitingPlaces(locationIndex, element.visitingPlaces);
    });

    this.sightSeeingForm.markAsPristine();
  }

  patchVisitingPlaces(locationIndex: number, visitingData: any) {
    const visitingPlacesArray = this.getVisitingPlacesArray(locationIndex);
    visitingPlacesArray.clear();
    visitingData.forEach((visitingPlace: any) => {
      let group = this.createVisitingPlacesFormGroup();
      group.patchValue(visitingPlace);
      visitingPlacesArray.push(group);
    });
  }



  //  Helper funtions
  createDestinationsFormArray(): any {
    if (this.packageDetailsFormData) {
      let data: FormGroup<any>[] = [];
      this.packageDetailsFormData.destinations?.forEach((destination: any) => {
        let formGroup = this.createLocationFormGroup();
        formGroup.patchValue({
          locationName: destination.location,
        })
        data.push(formGroup);
      });
      return data;
    } else {
      this._responseNotificationService.showMessage("Package Details is not entered", true)
      return [];
    }
  }

  getAvailableLocations(locationIndex: number): string[] {
    const selectedLocations = this.getLocationsArray().controls
      .map(control => control.get('locationName')?.value)
      .filter(value => value); // Remove empty values

    return this.destinationsArray.filter(location => !selectedLocations.includes(location) || this.getLocationsArray().at(locationIndex).get('locationName')?.value === location);
  }

  async onSubmit(): Promise<boolean> {
    let formData = this.serializeFormData(this.sightSeeingForm.value);

    return new Promise((resolve) => {
      this._dmcPackageService.addOptionalSightseeing(formData).subscribe({
        next: (data: any) => {
          this.storeFormData.emit(data);
        },
        error: (error) => {
          console.error('Error saving Pricing: ', error);
          resolve(false);
        },
        complete: () => {
          this.sightSeeingForm.markAsPristine();
          resolve(true);
        }
      });
    });
  }

  serializeFormData(formData: any) {
    const serializedData = {
      ...formData,
      enableSightseeing: this.formEnabled
    };
    return serializedData;
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  isFormValid(): boolean {
    if(this.formEnabled && this.sightSeeingForm.invalid){
      return false;
    }else{
      return true;
    }
  }

  formToggleChanged() {
    this.sightSeeingForm.markAsDirty();
    this.sightSeeingForm.markAsTouched();
  }
}
